/**
 * ============================================================================
 * useRequalificationScheduleQuery — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the requalification Kanban dashboard.
 *          Returns rows partitioned across PENDING / IN_PROGRESS / COMPLETED.
 * Endpoint: GET /api/v1/equipment/requalification-schedule
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  EquipmentCriticality,
  GampClass,
} from '../EquipmentWorkbench/useEquipmentQuery';
import type {
  RequalificationState,
  RequalificationTrigger,
} from '../EquipmentDetail/useEquipmentDetailQuery';

export interface RequalScheduleRow {
  requalification_id: string;
  equipment_id: string;
  equipment_tag: string;
  equipment_name: string;
  criticality: EquipmentCriticality;
  gamp_class: GampClass;
  trigger: RequalificationTrigger;
  scheduled_for_utc: string;
  state: RequalificationState;
  started_at_utc: string | null;
  completed_at_utc: string | null;
  validation_lead: string | null;
  qualification_document_id: string | null;
  /** Negative when overdue; positive when scheduled in the future. */
  days_until_due: number;
}

export interface RequalScheduleResponse {
  items: RequalScheduleRow[];
  counts: Record<RequalificationState, number>;
}

export function useRequalificationScheduleQuery(): UseQueryResult<RequalScheduleResponse> {
  return useQuery<RequalScheduleResponse>({
    queryKey: ['equipment', 'requalSchedule'],
    queryFn: ({ signal }) =>
      apiFetch<RequalScheduleResponse>(
        '/api/v1/equipment/requalification-schedule',
        { signal },
      ),
  });
}
